<script setup>
import { Form } from '@inertiajs/vue3'
import { ref, computed } from 'vue'
import { store as storeOrderDemand } from '@/actions/App/Http/Controllers/OrderDemandController'
import InputLabel from '@/components/InputLabel.vue'
import InputError from '@/components/InputError.vue'
import TextInput from '@/components/TextInput.vue'
import PrimaryButton from '@/components/PrimaryButton.vue'
import SecondaryButton from '@/components/SecondaryButton.vue'
import { ChevronLeftIcon } from '@heroicons/vue/24/solid'
import { XMarkIcon, PhotoIcon } from '@heroicons/vue/24/solid'
import { privacy } from '@/actions/App/Http/Controllers/LawController'

const currentStep = ref(1)
const totalSteps = 3

// Photo upload state
const uploadedPhotos = ref([])
const fileInputRef = ref(null)
const isDragging = ref(false)

function handlePhotoUpload(event) {
    const files = event.target.files
    if (!files) return
    addFiles(files)
}

function handleDrop(event) {
    event.preventDefault()
    isDragging.value = false
    const files = event.dataTransfer?.files
    if (files) {
        addFiles(files)
    }
}

function addFiles(files) {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png']
    const maxSize = 10 * 1024 * 1024 // 10 MB
    const maxFiles = 5

    for (const file of files) {
        if (uploadedPhotos.value.length >= maxFiles) {
            alert('Můžete nahrát maximálně 5 fotek.')
            break
        }
        if (!validTypes.includes(file.type)) {
            alert(`Soubor "${file.name}" není podporovaný formát. Povolené formáty: JPG, PNG.`)
            continue
        }
        if (file.size > maxSize) {
            alert(`Soubor "${file.name}" je příliš velký. Maximální velikost je 10 MB.`)
            continue
        }

        const reader = new FileReader()
        reader.onload = (e) => {
            uploadedPhotos.value.push({
                id: Date.now() + Math.random(),
                file: file,
                preview: e.target?.result,
                name: file.name,
                size: formatFileSize(file.size),
            })
            updateFileInput()
        }
        reader.readAsDataURL(file)
    }
}

function removePhoto(photoId) {
    uploadedPhotos.value = uploadedPhotos.value.filter((p) => p.id !== photoId)
    updateFileInput()
}

function removeAllPhotos() {
    uploadedPhotos.value = []
    updateFileInput()
}

function updateFileInput() {
    // Create a new DataTransfer to update the file input
    const dt = new DataTransfer()
    uploadedPhotos.value.forEach((photo) => {
        dt.items.add(photo.file)
    })
    if (fileInputRef.value) {
        fileInputRef.value.files = dt.files
    }
}

function formatFileSize(bytes) {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

const photosCount = computed(() => uploadedPhotos.value.length)
const canAddMorePhotos = computed(() => uploadedPhotos.value.length < 5)

const selectedServiceType = ref(null)

const serviceTypes = [
    {
        value: 'pravidelna_udrzba',
        label: 'Pravidelná údržba',
        description:
            'Doporučujeme objednávat sezónní kontrolu po skončení topné sezóny. V jejím průběhu se z důvodu zvýšeného počtu poruch a havárií věnujeme především servisním zásahům, a není v našich kapacitních možnostech zajistit v tomto období všechny kontroly v krátkých termínech. Proto doporučujeme objednávat kontroly v období od března do srpna.',
    },
    {
        value: 'servis_oprava',
        label: 'Servis / Oprava',
        description:
            'V některých případech nemusí být nefunkčnost plynového spotřebiče způsobena poruchou. Než nás kontaktujete, doporučujeme provést několik základních kontrol:',
        checklist: [
            'ověřte nastavení termostatu, případně vyměňte baterie (zejména před topnou sezónou)',
            'zkontrolujte, zda je otevřený přívod plynu',
            'ověřte funkčnost elektrické zásuvky (např. pomocí jiného spotřebiče)',
            'pokud používáte rozdvojku, zapojte kotel přímo do zásuvky',
            'zkontrolujte tlak topné vody – při poklesu pod doporučenou hodnotu (cca 1 bar) doplňte vodu do systému',
            'pokud kotel topí, ale radiátory zůstávají studené nebo jen vlažné, zkontrolujte stav topné vody a proveďte odvzdušnění radiátorů',
        ],
        footer: 'Pokud zařízení nefunguje ani po provedení těchto kroků, kontaktujte nás a domluvte si servisní zásah.',
    },
]

const selectedServiceDescription = computed(() => {
    return serviceTypes.find((s) => s.value === selectedServiceType.value) ?? null
})

const boilerTypes = [
    { value: 'plynovy', label: 'Plynový kotel' },
    { value: 'elektro', label: 'Elektro kotel' },
    { value: 'pevna_paliva', label: 'Kotel na pevná paliva' },
]

const boilerManufacturers = [
    { value: 'brotje', label: 'Brötje' },
    { value: 'viessmann', label: 'Viessmann' },
    { value: 'baxi', label: 'BAXI' },
    { value: 'protherm', label: 'Protherm' },
    { value: 'dakon', label: 'Dakon' },
    { value: 'junkers', label: 'Junkers' },
    { value: 'atmos', label: 'ATMOS' },
    { value: 'other', label: 'Jiný výrobce' },
]

const preferredTimeOptions = [
    { value: 'morning', label: 'Ráno (7:00 - 12:00)' },
    { value: 'afternoon', label: 'Odpoledne (12:00 - 17:00)' },
]

function nextStep() {
    if (currentStep.value < totalSteps) {
        currentStep.value++
    }
}

function prevStep() {
    if (currentStep.value > 1) {
        currentStep.value--
    }
}
</script>

<template>
    <div class="mx-auto w-full max-w-3xl">
        <div class="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
            <div class="mb-8 text-center">
                <h3 class="text-2xl font-bold text-gray-900">Objednávkový formulář</h3>
                <p class="mt-2 text-gray-500">Vyplňte poptávku a my vám připravíme nezávaznou nabídku.</p>
            </div>

            <Form
                :action="storeOrderDemand.url()"
                resetOnSuccess
                method="post"
                #default="{ errors, validate, validating, processing, recentlySuccessful }"
                :options="{
                    preserveScroll: true,
                    preserveState: true,
                }"
            >
                <!-- Progress Steps -->
                <div class="mb-8">
                    <div class="flex items-center justify-between">
                        <div
                            v-for="step in totalSteps"
                            :key="step"
                            class="flex items-center"
                            :class="{ 'flex-1': step < totalSteps }"
                        >
                            <div
                                class="flex size-10 items-center justify-center rounded-full text-sm font-semibold transition-colors"
                                :class="
                                    step <= currentStep ? 'bg-brand-primary text-white' : 'bg-gray-200 text-gray-500'
                                "
                            >
                                {{ step }}
                            </div>
                            <div
                                v-if="step < totalSteps"
                                class="mx-2 h-1 flex-1 rounded transition-colors"
                                :class="step < currentStep ? 'bg-brand-primary' : 'bg-gray-200'"
                            ></div>
                        </div>
                    </div>
                    <div class="mt-4 flex justify-between text-sm text-gray-500">
                        <span>Kontakt</span>
                        <span>Adresa</span>
                        <span>Služba</span>
                    </div>
                </div>

                <!-- Step 1: Contact Information -->
                <div
                    v-show="currentStep === 1"
                    class="space-y-6"
                >
                    <h4 class="text-lg font-semibold text-gray-900">Kontaktní údaje</h4>

                    <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <!-- First Name -->
                        <div>
                            <InputLabel
                                for="first_name"
                                value="Jméno"
                                :is-required="true"
                                variant="light"
                            />
                            <TextInput
                                id="first_name"
                                name="first_name"
                                type="text"
                                placeholder="Jan"
                                autocomplete="given-name"
                                variant="light"
                                :has-error="!!errors.first_name"
                            />
                            <InputError
                                class="mt-1"
                                :message="errors.first_name"
                            />
                        </div>

                        <!-- Last Name -->
                        <div>
                            <InputLabel
                                for="last_name"
                                value="Příjmení"
                                :is-required="true"
                                variant="light"
                            />
                            <TextInput
                                id="last_name"
                                name="last_name"
                                type="text"
                                placeholder="Novák"
                                autocomplete="family-name"
                                variant="light"
                                :has-error="!!errors.last_name"
                            />
                            <InputError
                                class="mt-1"
                                :message="errors.last_name"
                            />
                        </div>
                    </div>

                    <!-- Email -->
                    <div>
                        <InputLabel
                            for="email"
                            value="E-mail"
                            :is-required="true"
                            variant="light"
                        />
                        <TextInput
                            id="email"
                            name="email"
                            type="email"
                            icon="email"
                            placeholder="jan.novak@email.cz"
                            autocomplete="email"
                            variant="light"
                            :has-error="!!errors.email"
                        />
                        <InputError
                            class="mt-1"
                            :message="errors.email"
                        />
                    </div>

                    <!-- Phone -->
                    <div>
                        <InputLabel
                            for="phone"
                            value="Telefon"
                            :is-required="true"
                            variant="light"
                        />
                        <TextInput
                            id="phone"
                            name="phone"
                            type="tel"
                            icon="phone"
                            placeholder="+420 123 456 789"
                            autocomplete="tel"
                            variant="light"
                            :has-error="!!errors.phone"
                        />
                        <InputError
                            class="mt-1"
                            :message="errors.phone"
                        />
                    </div>

                    <!-- Note -->
                    <div>
                        <InputLabel
                            for="note"
                            value="Poznámka"
                            variant="light"
                        />
                        <textarea
                            id="note"
                            name="note"
                            rows="3"
                            placeholder="Máte nějaké další informace pro nás?"
                            class="focus:border-brand-primary focus:ring-brand-primary block w-full rounded-lg border border-gray-300 px-4 py-3 text-base text-gray-900 placeholder:text-gray-400 focus:ring-1 focus:outline-none"
                            :class="{ 'border-red-500': errors.note }"
                        />
                        <InputError
                            class="mt-1"
                            :message="errors.note"
                        />
                    </div>

                    <div class="mt-8 flex items-center justify-end">
                        <PrimaryButton
                            type="button"
                            class="w-full"
                            :disabled="validating"
                            @click="
                                validate({
                                    only: ['first_name', 'last_name', 'email', 'phone'],
                                    onSuccess: () => nextStep(),
                                })
                            "
                        >
                            {{ validating ? 'Ověřuji...' : 'Pokračovat' }}
                        </PrimaryButton>
                    </div>
                </div>

                <!-- Step 2: Address -->
                <div
                    v-show="currentStep === 2"
                    class="space-y-6"
                >
                    <div class="border border-red-200 bg-red-50 p-4">
                        <h4 class="text-lg font-semibold text-gray-900">Adresa realizace</h4>
                        <p>
                            Uveďte prosím adresu, na které se nachází plynový kotel nebo zařízení, které potřebujete
                            servisovat.
                        </p>
                    </div>

                    <!-- Street -->
                    <div>
                        <InputLabel
                            for="street"
                            value="Ulice a číslo popisné"
                            :is-required="true"
                            variant="light"
                        />
                        <TextInput
                            id="street"
                            name="street"
                            type="text"
                            placeholder="Příkladová 123"
                            autocomplete="street-address"
                            variant="light"
                            :has-error="!!errors.street"
                        />
                        <InputError
                            class="mt-1"
                            :message="errors.street"
                        />
                    </div>

                    <!-- City -->
                    <div>
                        <InputLabel
                            for="city"
                            value="Město"
                            :is-required="true"
                            variant="light"
                        />
                        <TextInput
                            id="city"
                            name="city"
                            type="text"
                            placeholder="Drnholec"
                            autocomplete="address-level2"
                            variant="light"
                            :has-error="!!errors.city"
                        />
                        <InputError
                            class="mt-1"
                            :message="errors.city"
                        />
                    </div>

                    <!-- Postal Code -->
                    <div>
                        <InputLabel
                            for="postal_code"
                            value="PSČ"
                            :is-required="true"
                            variant="light"
                        />
                        <TextInput
                            id="postal_code"
                            name="postal_code"
                            type="text"
                            placeholder="123 45"
                            autocomplete="postal-code"
                            variant="light"
                            :has-error="!!errors.postal_code"
                        />
                        <InputError
                            class="mt-1"
                            :message="errors.postal_code"
                        />
                    </div>

                    <div class="mt-8 flex items-center justify-start gap-2">
                        <SecondaryButton
                            type="button"
                            @click="prevStep"
                        >
                            <ChevronLeftIcon class="mr-2 size-4" />
                            Zpět
                        </SecondaryButton>

                        <PrimaryButton
                            type="button"
                            class="w-full"
                            :disabled="validating"
                            @click="
                                validate({
                                    only: ['street', 'city', 'postal_code'],
                                    onSuccess: () => nextStep(),
                                })
                            "
                        >
                            {{ validating ? 'Ověřuji...' : 'Pokračovat' }}
                        </PrimaryButton>
                    </div>
                </div>

                <!-- Step 3: Service Details -->
                <div
                    v-show="currentStep === 3"
                    class="space-y-6"
                >
                    <h4 class="text-lg font-semibold text-gray-900">Informace o službě</h4>

                    <!-- Service Type -->
                    <div>
                        <InputLabel
                            for="service_type"
                            value="Typ služby"
                            :is-required="true"
                            variant="light"
                        />
                        <div class="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <label
                                v-for="service in serviceTypes"
                                :key="service.value"
                                class="hover:border-brand-primary/50 has-checked:border-brand-primary has-checked:bg-brand-primary/5 relative flex cursor-pointer items-center justify-center rounded-xl border-2 border-gray-200 p-4 transition-all"
                            >
                                <input
                                    v-model="selectedServiceType"
                                    type="radio"
                                    name="service_type"
                                    :value="service.value"
                                    class="peer sr-only"
                                />
                                <span class="peer-checked:text-brand-primary text-center font-medium text-gray-700">
                                    {{ service.label }}
                                </span>
                            </label>
                        </div>

                        <!-- Service Type Description -->
                        <div
                            v-if="selectedServiceDescription"
                            class="mt-3 rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600"
                        >
                            <p>{{ selectedServiceDescription.description }}</p>
                            <ul
                                v-if="selectedServiceDescription.checklist"
                                class="mt-2 list-disc space-y-1 pl-5"
                            >
                                <li
                                    v-for="(item, index) in selectedServiceDescription.checklist"
                                    :key="index"
                                >
                                    {{ item }}
                                </li>
                            </ul>
                            <p
                                v-if="selectedServiceDescription.footer"
                                class="mt-3 font-medium text-gray-700"
                            >
                                {{ selectedServiceDescription.footer }}
                            </p>
                        </div>

                        <InputError
                            class="mt-1"
                            :message="errors.service_type"
                        />
                    </div>

                    <!-- Boiler Type -->
                    <div>
                        <InputLabel
                            for="boiler_type"
                            value="Typ kotle"
                            :is-required="true"
                            variant="light"
                        />
                        <div class="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-3">
                            <label
                                v-for="boilerType in boilerTypes"
                                :key="boilerType.value"
                                class="hover:border-brand-primary/50 has-checked:border-brand-primary has-checked:bg-brand-primary/5 relative flex cursor-pointer items-center justify-center rounded-xl border-2 border-gray-200 p-3 transition-all"
                            >
                                <input
                                    type="radio"
                                    name="boiler_type"
                                    :value="boilerType.value"
                                    class="peer sr-only"
                                />
                                <span class="peer-checked:text-brand-primary text-center font-medium text-gray-700">
                                    {{ boilerType.label }}
                                </span>
                            </label>
                        </div>
                        <InputError
                            class="mt-1"
                            :message="errors.boiler_type"
                        />
                    </div>

                    <!-- Boiler Manufacturer -->
                    <div>
                        <InputLabel
                            for="boiler_manufacturer"
                            value="Výrobce kotle"
                            :is-required="true"
                            variant="light"
                        />
                        <div class="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
                            <label
                                v-for="manufacturer in boilerManufacturers"
                                :key="manufacturer.value"
                                class="hover:border-brand-primary/50 has-checked:border-brand-primary has-checked:bg-brand-primary/5 relative flex cursor-pointer items-center justify-center rounded-xl border-2 border-gray-200 p-3 transition-all"
                            >
                                <input
                                    type="radio"
                                    name="boiler_manufacturer"
                                    :value="manufacturer.value"
                                    class="peer sr-only"
                                />
                                <span class="peer-checked:text-brand-primary text-center font-medium text-gray-700">
                                    {{ manufacturer.label }}
                                </span>
                            </label>
                        </div>
                        <InputError
                            class="mt-1"
                            :message="errors.boiler_manufacturer"
                        />
                    </div>

                    <!-- Boiler Model (optional) -->
                    <div>
                        <InputLabel
                            for="boiler_model"
                            value="Model a typ kotle"
                            variant="light"
                        />
                        <TextInput
                            id="boiler_model"
                            name="boiler_model"
                            type="text"
                            placeholder="např. Vitodens 100-W, ecoTEC plus..."
                            variant="light"
                            :has-error="!!errors.boiler_model"
                        />
                        <InputError
                            class="mt-1"
                            :message="errors.boiler_model"
                        />
                    </div>

                    <!-- Last Service Date (optional) -->
                    <div>
                        <InputLabel
                            for="last_service_date"
                            value="Datum posledního servisu"
                            variant="light"
                        />
                        <TextInput
                            id="last_service_date"
                            name="last_service_date"
                            type="date"
                            variant="light"
                            :has-error="!!errors.last_service_date"
                        />
                        <InputError
                            class="mt-1"
                            :message="errors.last_service_date"
                        />
                    </div>

                    <!-- Boiler Photos -->
                    <div>
                        <InputLabel
                            for="boiler_photos"
                            value="Fotografie kotle"
                            variant="light"
                        />
                        <p class="mt-1 text-sm text-gray-500">
                            Nahrajte prosím fotografie kotle (z dálky, z boku, z vrchu)
                        </p>

                        <!-- Upload Zone -->
                        <div
                            v-if="canAddMorePhotos"
                            class="mt-2"
                        >
                            <label
                                for="boiler_photos"
                                class="group relative flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-6 transition-all"
                                :class="
                                    isDragging
                                        ? 'border-brand-primary bg-brand-primary/5'
                                        : 'hover:border-brand-primary/50 border-gray-300 bg-gray-50 hover:bg-gray-100'
                                "
                                @dragover.prevent="isDragging = true"
                                @dragleave.prevent="isDragging = false"
                                @drop="handleDrop"
                            >
                                <PhotoIcon
                                    class="mb-3 size-10 transition-colors"
                                    :class="
                                        isDragging
                                            ? 'text-brand-primary'
                                            : 'group-hover:text-brand-primary text-gray-400'
                                    "
                                />
                                <span
                                    class="text-sm font-medium transition-colors"
                                    :class="
                                        isDragging
                                            ? 'text-brand-primary'
                                            : 'group-hover:text-brand-primary text-gray-600'
                                    "
                                >
                                    {{ isDragging ? 'Přetáhněte sem fotky' : 'Klikněte nebo přetáhněte fotky' }}
                                </span>
                                <span class="mt-1 text-xs text-gray-500">
                                    PNG, JPG do 10 MB ({{ photosCount }}/5 fotek)
                                </span>
                                <input
                                    id="boiler_photos"
                                    ref="fileInputRef"
                                    name="boiler_photos[]"
                                    type="file"
                                    accept="image/png,image/jpeg,image/jpg"
                                    multiple
                                    class="sr-only"
                                    @change="handlePhotoUpload"
                                />
                            </label>
                        </div>

                        <!-- Max files reached message -->
                        <div
                            v-else
                            class="mt-2 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 p-4 text-center"
                        >
                            <p class="text-sm text-gray-500">Maximální počet fotek (5) byl dosažen.</p>
                        </div>

                        <!-- Photo Preview Grid -->
                        <div
                            v-if="uploadedPhotos.length > 0"
                            class="mt-4"
                        >
                            <div class="mb-2 flex items-center justify-between">
                                <span class="text-sm font-medium text-gray-700">
                                    Nahrané fotky ({{ photosCount }})
                                </span>
                                <button
                                    type="button"
                                    class="text-sm text-red-600 hover:text-red-700 hover:underline"
                                    @click="removeAllPhotos"
                                >
                                    Odstranit vše
                                </button>
                            </div>
                            <div class="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5">
                                <div
                                    v-for="photo in uploadedPhotos"
                                    :key="photo.id"
                                    class="group relative aspect-square overflow-hidden rounded-lg border border-gray-200 bg-gray-100"
                                >
                                    <img
                                        :src="photo.preview"
                                        :alt="photo.name"
                                        class="size-full object-cover"
                                    />
                                    <!-- Overlay with info -->
                                    <div
                                        class="absolute inset-0 flex flex-col justify-end bg-linear-to-t from-black/60 via-transparent to-transparent opacity-0 transition-opacity group-hover:opacity-100"
                                    >
                                        <div class="p-2">
                                            <p class="truncate text-xs font-medium text-white">{{ photo.name }}</p>
                                            <p class="text-xs text-white/70">{{ photo.size }}</p>
                                        </div>
                                    </div>
                                    <!-- Remove button -->
                                    <button
                                        type="button"
                                        class="absolute top-1 right-1 flex size-6 items-center justify-center rounded-full bg-red-500 text-white opacity-0 shadow-sm transition-opacity group-hover:opacity-100 hover:bg-red-600"
                                        @click="removePhoto(photo.id)"
                                    >
                                        <XMarkIcon class="size-4" />
                                    </button>
                                    <!-- Success checkmark -->
                                    <div
                                        class="absolute right-1 bottom-1 flex size-5 items-center justify-center rounded-full bg-green-500 text-white shadow-sm"
                                    >
                                        <svg
                                            class="size-3"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke-width="3"
                                            stroke="currentColor"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                d="M4.5 12.75l6 6 9-13.5"
                                            />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <InputError
                            class="mt-1"
                            :message="errors['boiler_photos']"
                        />
                        <InputError
                            class="mt-1"
                            :message="errors['boiler_photos.0']"
                        />
                        <InputError
                            class="mt-1"
                            :message="errors['boiler_photos.1']"
                        />
                        <InputError
                            class="mt-1"
                            :message="errors['boiler_photos.2']"
                        />
                        <InputError
                            class="mt-1"
                            :message="errors['boiler_photos.3']"
                        />
                        <InputError
                            class="mt-1"
                            :message="errors['boiler_photos.4']"
                        />
                    </div>

                    <!-- Problem Description -->
                    <div>
                        <InputLabel
                            for="problem_description"
                            value="Zpráva / Popis požadavku"
                            variant="light"
                        />
                        <textarea
                            id="problem_description"
                            name="problem_description"
                            rows="4"
                            placeholder="Popište co nejvíce detailů o vašem problému nebo požadavku..."
                            class="focus:border-brand-primary focus:ring-brand-primary block w-full rounded-lg border border-gray-300 px-4 py-3 text-base text-gray-900 placeholder:text-gray-400 focus:ring-1 focus:outline-none"
                            :class="{ 'border-red-500': errors.problem_description }"
                        />
                        <InputError
                            class="mt-1"
                            :message="errors.problem_description"
                        />
                    </div>

                    <!-- Preferred Date -->
                    <div>
                        <InputLabel
                            for="preferred_date"
                            value="Preferovaný termín"
                            variant="light"
                        />
                        <TextInput
                            id="preferred_date"
                            name="preferred_date"
                            type="date"
                            variant="light"
                            :has-error="!!errors.preferred_date"
                        />
                        <InputError
                            class="mt-1"
                            :message="errors.preferred_date"
                        />
                    </div>

                    <!-- Preferred Time -->
                    <div>
                        <InputLabel
                            for="preferred_time"
                            value="Preferovaný čas"
                            variant="light"
                        />
                        <div class="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <label
                                v-for="time in preferredTimeOptions"
                                :key="time.value"
                                class="hover:border-brand-primary/50 has-checked:border-brand-primary has-checked:bg-brand-primary/5 relative flex cursor-pointer items-center justify-center rounded-xl border-2 border-gray-200 p-3 text-sm transition-all"
                            >
                                <input
                                    type="radio"
                                    name="preferred_time"
                                    :value="time.value"
                                    class="peer sr-only"
                                />
                                <span class="peer-checked:text-brand-primary text-center font-medium text-gray-700">
                                    {{ time.label }}
                                </span>
                            </label>
                        </div>
                        <InputError
                            class="mt-1"
                            :message="errors.preferred_time"
                        />
                    </div>

                    <!-- Consents -->
                    <div class="space-y-4 rounded-xl bg-gray-50 p-4">
                        <div class="flex items-start gap-3">
                            <input
                                id="gdpr_consent"
                                name="gdpr_consent"
                                type="checkbox"
                                value="1"
                                class="text-brand-primary focus:ring-brand-primary size-4 rounded border-gray-300"
                            />
                            <label
                                for="gdpr_consent"
                                class="text-sm text-gray-600"
                            >
                                Souhlasím se
                                <a
                                    :href="privacy.url()"
                                    aria-label="Odkaz na Zásady ochrany osobních údajů"
                                    class="text-brand-primary hover:underline"
                                    target="_blank"
                                    >zpracováním osobních údajů</a
                                >
                                za účelem vyřízení mé poptávky.
                                <span class="text-red-500">*</span>
                            </label>
                        </div>
                        <InputError
                            class="mt-1"
                            :message="errors.gdpr_consent"
                        />

                        <!-- <div class="flex items-start gap-3">
                            <input
                                id="marketing_consent"
                                name="marketing_consent"
                                type="checkbox"
                                value="1"
                                class="text-brand-primary focus:ring-brand-primary mt-1 size-4 rounded border-gray-300"
                            />
                            <label
                                for="marketing_consent"
                                class="text-sm text-gray-600"
                            >
                                Souhlasím se zasíláním obchodních sdělení a novinek.
                            </label>
                        </div> -->
                    </div>

                    <ul>
                        <li
                            v-for="(error, field) in errors"
                            :key="field"
                        >
                            {{ error }}
                        </li>
                    </ul>

                    <div class="mt-8 flex items-center justify-start gap-2">
                        <SecondaryButton
                            type="button"
                            @click="prevStep"
                        >
                            <ChevronLeftIcon class="mr-2 size-4" />
                            Zpět
                        </SecondaryButton>

                        <PrimaryButton
                            type="submit"
                            :disabled="processing || validating"
                            :success="recentlySuccessful"
                            class="w-full"
                        >
                            {{ processing ? 'Odesílání...' : 'Odeslat poptávku' }}
                        </PrimaryButton>
                    </div>
                </div>
            </Form>
        </div>
    </div>
</template>
